import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { useModel } from '@/features/models/hooks';
import { FileDetailView } from '@/features/models/components/FileDetailView';
import { Button } from '@/components/Button';
import { modelsApi } from '@/features/models/api';
import { useToast } from '@/components/ToastProvider';

const isDevelopment = import.meta.env.MODE === 'development';

export function ModelFileDetailPage() {
  const { modelId, fileId } = useParams<{ modelId: string; fileId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [isReprocessing, setIsReprocessing] = useState(false);

  // Fetch model to get file details
  const { data: model, isLoading: modelLoading } = useModel(modelId!);

  if (!modelId || !fileId) {
    navigate('/models');
    return null;
  }

  const file = model?.files.find((f) => f.id === fileId);

  const handleDownload = async () => {
    if (!file) return;

    try {
      const { url } = await modelsApi.getFileDownloadUrl(modelId, fileId);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleReprocess = async () => {
    if (!fileId) return;

    try {
      setIsReprocessing(true);
      await modelsApi.reprocessFile(fileId);
      toast.success('File reprocessing queued. The page will update automatically when complete.');
    } catch (error) {
      toast.error('Failed to queue file reprocessing');
    } finally {
      setIsReprocessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background-subtle">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
                onClick={() => navigate(`/models/${modelId}`)}
              >
                Back to Model
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-lg font-semibold text-foreground">
                  {modelLoading ? 'Loading...' : model?.name}
                </h1>
                {file && (
                  <p className="text-sm text-foreground-muted mt-0.5">{file.fileName}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isDevelopment && file && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<RefreshCw className="h-4 w-4" />}
                  onClick={handleReprocess}
                  disabled={isReprocessing}
                  loading={isReprocessing}
                >
                  Reprocess
                </Button>
              )}
              {/* Temporarily hidden - 3D Viewer under development
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Eye className="h-4 w-4" />}
                onClick={handleView3D}
                disabled={!file}
              >
                3D Viewer
              </Button>
              */}
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleDownload}
                disabled={!file}
              >
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {file ? (
          <FileDetailView modelId={modelId} file={file} />
        ) : (
          <div className="flex h-96 items-center justify-center">
            <p className="text-foreground-muted">
              {modelLoading ? 'Loading file...' : 'File not found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
